import { ActionIcon, Box, Button, Card, Group, Select, Stack, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash, IconGripVertical } from '@tabler/icons-react';
import { ExerciseTypeFields } from './ExerciseTypeFields';

interface ExerciseBuilderProps {
	form: UseFormReturnType<any>;
	languages: readonly string[];
	languageLabels: Record<string, string>;
}

export function ExerciseBuilder({ form, languages, languageLabels }: ExerciseBuilderProps) {
	const workoutType = form.values.type;

	const addExercise = () => {
		let defaultTypeData;
		if (workoutType === 'REFLECTION_INPUT') {
			defaultTypeData = {
				inputs: [{
					translations: languages.map(lang => ({
						language: lang,
						title: '',
						placeholder: ''
					}))
				}]
			};
		} else if (workoutType === 'AUDIO' || workoutType === 'VIDEO') {
			defaultTypeData = { url: null, duration: 0, fileId: null, mediaUrls: [] };
		} else if (workoutType === 'BREATHING') {
			defaultTypeData = { inhale: 4, hold: 4, exhale: 6, rounds: 10, mediaUrl: null, mediaUrls: [] };
		} else if (workoutType === 'IMAGE_UPLOAD') {
			defaultTypeData = { minImages: 1, maxImages: 3 };
		} else {
			defaultTypeData = {
				inputs: [{
					translations: languages.map(lang => ({
						language: lang,
						title: '',
						placeholder: ''
					}))
				}]
			};
		}

		form.insertListItem('exercises', {
			translations: languages.map(lang => ({
				language: lang,
				name: '',
				description: '',
				tips: ['']
			})),
			sortOrder: form.values.exercises.length + 1,
			groupId: null,
			durationMin: null,
			durationMax: null,
			typeData: defaultTypeData,
		});
	};

	const removeExercise = (index: number) => {
		form.removeListItem('exercises', index);
	};

	return (
		<Stack gap="md">
			<Group justify="space-between">
				<Title order={4}>Exercises</Title>
				<Button leftSection={<IconPlus size={16} />} variant="light" onClick={addExercise}>
					Add Exercise
				</Button>
			</Group>

			{form.values.exercises.length === 0 && (
				<Text c="dimmed" size="sm">
					No exercises added yet. Click "Add Exercise" to get started.
				</Text>
			)}

			{form.values.exercises.map((exercise: any, index: number) => (
				<Card key={index} withBorder>
					<Stack gap="md">
						<Group justify="space-between">
							<Group gap="xs">
								<ActionIcon variant="subtle" style={{ cursor: 'grab' }}>
									<IconGripVertical size={16} />
								</ActionIcon>
								<Text fw={500}>Exercise {index + 1}</Text>
							</Group>
							<ActionIcon
								color="red"
								variant="subtle"
								onClick={() => removeExercise(index)}
							>
								<IconTrash size={16} />
							</ActionIcon>
						</Group>

						<ExerciseTypeFields
							form={form}
							exerciseIndex={index}
							exerciseType={workoutType}
							languages={languages}
							languageLabels={languageLabels}
						/>
					</Stack>
				</Card>
			))}
		</Stack>
	);
}
